import React, { useContext } from 'react';
import { Box, Container, Heading, Text, Button, Stack, Image } from '@chakra-ui/react';
import { Link } from 'react-router-dom';
import HeroImage from '../../assets/HeroImage.png';
import ThemeContext from '../../context/ThemeContext';

const HeroSection = () => {
  const { themeData } = useContext(ThemeContext);

  // Extract theme colors with fallbacks
  const primaryColor = themeData?.colors?.primary || '#2B6CB0';
  const secondaryColor = themeData?.colors?.secondary || '#2C5282';
  const accentColor = themeData?.colors?.accent || '#63B3ED';
  const backgroundColor = themeData?.colors?.background || '#F7FAFC';

  // Determine if theme is dark (midnight)
  const isDarkTheme = themeData?.id === 'midnight';

  // Theme-aware colors
  const headingTextColor = isDarkTheme ? '#F7FAFC' : 'gray.900';
  const bodyTextColor = isDarkTheme ? '#E2E8F0' : 'gray.600';
  const badgeTextColor = isDarkTheme ? accentColor : primaryColor;
  const buttonHoverBg = isDarkTheme ? accentColor : secondaryColor;

  return (
    <Box as="section" py={{ base: 10, md: 16 }} bg={backgroundColor}>
      <Container maxW="container.xl">
        <Stack
          direction={{ base: 'column', md: 'row' }}
          spacing={{ base: 12, md: 16 }}
          align="center"
          justify="space-between"
        >
          {/* Left Content */}
          <Box flex="1" textAlign={{ base: 'center', md: 'left' }}>
            <Text
              fontSize="sm"
              fontWeight="bold"
              color={badgeTextColor}
              mb={3}
              textTransform="uppercase"
              letterSpacing="wider"
            >
              AI-Powered Behavioral Bias Detection
            </Text>
            <Heading
              as="h1"
              size="2xl"
              fontWeight="bold"
              mb={4}
              color={headingTextColor}
              lineHeight="shorter"
            >
              Transform Client Relationships with Behavioral Intelligence
            </Heading>
            <Text 
              fontSize="lg" 
              color={bodyTextColor} 
              mb={8}
              lineHeight="tall"
            >
              FinanSee empowers family offices, wealth managers, and financial institutions 
              to identify behavioral biases in client portfolios—enabling personalized advice, 
              risk mitigation, and better outcomes through advanced AI analytics.
            </Text>

            <Stack
              direction={{ base: 'column', sm: 'row' }}
              spacing={4}
              justify={{ base: 'center', md: 'flex-start' }}
            >
              <Button
                as={Link}
                to="/login"
                bg={primaryColor}
                color="white"
                size="lg"
                px={8}
                fontWeight="semibold"
                _hover={{
                  bg: buttonHoverBg,
                  transform: 'translateY(-2px)',
                  boxShadow: 'lg'
                }}
                _active={{
                  bg: secondaryColor,
                  transform: 'translateY(0)'
                }}
                transition="all 0.2s"
              >
                Access Platform
              </Button>
              <Button
                as={Link}
                to="/docs"
                variant="outline"
                size="lg"
                px={8}
                fontWeight="semibold"
                borderColor={primaryColor}
                color={primaryColor}
                _hover={{
                  bg: isDarkTheme ? 'rgba(66, 153, 225, 0.1)' : 'rgba(43, 108, 176, 0.1)',
                  borderColor: accentColor,
                  color: accentColor,
                  transform: 'translateY(-2px)',
                  boxShadow: 'lg'
                }}
                _active={{
                  transform: 'translateY(0)'
                }}
                transition="all 0.2s"
              >
                View Documentation
              </Button>
            </Stack>
          </Box>

          {/* Right Image */}
          <Box flex="1" textAlign="center">
            <Image
              src={HeroImage}
              alt="FinanSee Platform Hero"
              borderRadius="lg"
              mx="auto"
              maxW={{ base: '80%', md: '100%' }}
              boxShadow={isDarkTheme ? 'dark-lg' : 'xl'}
              border={isDarkTheme ? '1px solid' : 'none'}
              borderColor={isDarkTheme ? 'rgba(66, 153, 225, 0.2)' : 'transparent'}
            />
          </Box>
        </Stack>
      </Container>
    </Box>
  );
};

export default HeroSection;